import React from 'react';
import { Box } from '@mui/material';
import FieldRow from './FieldRow';
import LocalizedField from './LocalizedField';
import SectionBlock from './SectionBlock';
import ImagePreview from './ImagePreview';
import { formatMinor, customerPrice } from './buildComparisonRows';

export default function PackageDetailPanel({ data, diffKeys = new Set(), side, lookups }) {
  if (!data) return null;

  const price = data.prices?.[0];
  const tax = data.tax
    ? { name: data.tax.name, rate: data.tax.ratePct, inclusive: data.tax.isInclusive }
    : null;
  const money = (minor) => formatMinor(minor, price?.currencyId, lookups);
  const thumbUrl = data.thumbnail?.linkLocalizations?.[0]?.mediaLink;
  const members = Array.isArray(data.members) ? data.members : [];

  return (
    <Box>
      <SectionBlock title="Package" />
      <LocalizedField
        label="Name"
        fieldKey="nameLocalizations"
        data={data.nameLocalizations}
        diffKeys={diffKeys}
        side={side}
      />
      <LocalizedField
        label="Description"
        fieldKey="descriptionLocalizations"
        data={data.descriptionLocalizations}
        diffKeys={diffKeys}
        side={side}
      />
      <FieldRow
        label="Thumbnail"
        value={thumbUrl}
        isChanged={diffKeys.has('thumbnailMedia')}
        side={side}
      />
      {thumbUrl && (
        <Box sx={{ px: 2, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <ImagePreview url={thumbUrl} />
        </Box>
      )}

      <SectionBlock title="Pricing" />
      <FieldRow
        label="Price"
        value={price ? money(price.baseCost) : null}
        isChanged={diffKeys.has('prices') || diffKeys.has('baseCost') || diffKeys.has('currencyId')}
        side={side}
      />
      <FieldRow
        label="Tax"
        value={tax ? `${tax.name} (${tax.rate}%, ${tax.inclusive ? 'Inclusive' : 'Exclusive'})` : 'None'}
        isChanged={diffKeys.has('tax')}
        side={side}
      />
      {price && (
        <FieldRow
          label="Customer price"
          value={money(customerPrice(price.baseCost, tax))}
          isChanged={false}
          side={side}
        />
      )}

      <SectionBlock title="Items in package" />
      {members.length > 0 ? (
        members.map((m) => (
          <FieldRow
            key={m.packageItemId ?? m.itemId}
            label={m.name ?? `Item ${m.itemId}`}
            value={[`× ${m.quantity ?? 1}`, m.itemTypeName].filter(Boolean).join(' · ')}
            side={side}
          />
        ))
      ) : (
        <FieldRow
          label="Items"
          value={data.memberCount != null ? `${data.memberCount} item${data.memberCount === 1 ? '' : 's'}` : 'None yet'}
          side={side}
        />
      )}

      <FieldRow
        label="Available"
        value={data.isAvailable ? 'Yes' : 'No'}
        isChanged={diffKeys.has('isAvailable')}
        side={side}
      />
    </Box>
  );
}
